import { 
  EnergyData, 
  EnergySummary, 
  ComprehensiveEnergyData,
  BatteryStatus,
  EVChargingStatus,
  HeatPumpStatus,
  BuildingZones,
  GridStatus,
  EnvironmentalData,
  SystemHealth
} from '../types/energyData';
import { startOfDay, endOfDay } from 'date-fns';

export class EnergyDataService {
  private data: EnergyData[] = [];

  constructor(data: EnergyData[]) {
    this.data = data;
  }

  // Get PV production for today (using actual data range)
  getPVProductionToday(): number {
    const pvFiles = this.data.filter(d => 
      d.tag.id.includes('PV') && 
      d.tag.id.includes('Power') && 
      !d.tag.id.includes('Expected')
    );
    
    if (pvFiles.length === 0) return 0;

    // Use the actual data range instead of "today"
    const allSamples = pvFiles.flatMap(file => file.samples);
    if (allSamples.length === 0) return 0;

    // Get the date range from the data
    const timestamps = allSamples.map(s => s.utc).sort((a, b) => a - b);
    const dataStart = timestamps[0];
    
    // Use the first day of data as "today"
    const startOfDataDay = startOfDay(new Date(dataStart)).getTime();
    const endOfDataDay = endOfDay(new Date(dataStart)).getTime();

    let totalProduction = 0;
    pvFiles.forEach(file => {
      const daySamples = file.samples.filter(sample => 
        sample.utc >= startOfDataDay && sample.utc <= endOfDataDay
      );
      
      // Sum up power values (assuming hourly samples)
      totalProduction += daySamples.reduce((sum, sample) => sum + sample.value, 0);
    });

    return Math.round(totalProduction / 1000); // Convert to kWh
  }

  // Get electricity consumption for today (using actual data range)
  getConsumptionToday(): number {
    const consumptionFiles = this.data.filter(d => 
      d.tag.id.includes('Consumer') && 
      d.tag.id.includes('Power') && 
      !d.tag.id.includes('Expected')
    );

    if (consumptionFiles.length === 0) return 0;

    // Use the actual data range instead of "today"
    const allSamples = consumptionFiles.flatMap(file => file.samples);
    if (allSamples.length === 0) return 0;

    // Get the date range from the data
    const timestamps = allSamples.map(s => s.utc).sort((a, b) => a - b);
    const dataStart = timestamps[0];
    
    // Use the first day of data as "today"
    const startOfDataDay = startOfDay(new Date(dataStart)).getTime();
    const endOfDataDay = endOfDay(new Date(dataStart)).getTime();

    let totalConsumption = 0;
    consumptionFiles.forEach(file => {
      const daySamples = file.samples.filter(sample => 
        sample.utc >= startOfDataDay && sample.utc <= endOfDataDay
      );
      
      totalConsumption += daySamples.reduce((sum, sample) => sum + sample.value, 0);
    });

    return Math.round(totalConsumption / 1000); // Convert to kWh
  }

  // Get consumption for last 7 days (using actual data range)
  getConsumption7Days(): number {
    const consumptionFiles = this.data.filter(d => 
      d.tag.id.includes('Consumer') && 
      d.tag.id.includes('Power') && 
      !d.tag.id.includes('Expected')
    );

    if (consumptionFiles.length === 0) return 0;

    // Use the actual data range instead of "last 7 days"
    const allSamples = consumptionFiles.flatMap(file => file.samples);
    if (allSamples.length === 0) return 0;

    // Get the date range from the data
    const timestamps = allSamples.map(s => s.utc).sort((a, b) => a - b);
    const dataStart = timestamps[0];
    const dataEnd = timestamps[timestamps.length - 1];

    let totalConsumption = 0;
    consumptionFiles.forEach(file => {
      const allDataSamples = file.samples.filter(sample => 
        sample.utc >= dataStart && sample.utc <= dataEnd
      );
      
      // Sum up power values (same approach as getConsumptionToday)
      totalConsumption += allDataSamples.reduce((sum, sample) => sum + sample.value, 0);
    });

    return Math.round(totalConsumption / 1000); // Convert to kWh
  }

  // Get current room temperatures
  // Optionally accepts a translation function for localized room names
  getRoomTemperatures(t?: any): Array<{ room: string; temperature: number; unit: string }> {
    const temperatureFiles = this.data.filter(d => 
      d.tag.id.includes('Temp') && 
      d.tag.unit === 'degC' &&
      !d.tag.id.includes('Expected') &&
      !d.tag.id.includes('SetPoint')
    );

    const rooms: Array<{ room: string; temperature: number; unit: string }> = [];

    temperatureFiles.forEach(file => {
      if (file.samples.length > 0) {
        const latestSample = file.samples[file.samples.length - 1];
        const roomName = this.getRoomName(file.tag.id, t);
        rooms.push({
          room: roomName,
          temperature: Math.round(latestSample.value * 10) / 10,
          unit: file.tag.unit
        });
      }
    });

    return rooms;
  }

  // Get energy summary
  getEnergySummary(): EnergySummary {
    return {
      pvProductionToday: this.getPVProductionToday(),
      consumptionToday: this.getConsumptionToday(),
      consumption7Days: this.getConsumption7Days(),
      roomTemperatures: this.getRoomTemperatures()
    };
  }

  private getRoomName(tagId: string, t?: any): string {
    // Use translation function if provided, otherwise return English fallback
    const translate = (key: string): string => {
      if (t) {
        const result = t(key);
        // Ensure we return a string (i18next can return objects)
        return typeof result === 'string' ? result : String(result);
      }
      // Fallback translations in English
      const fallbacks: { [key: string]: string } = {
        'building.thermalZone1': 'Living Room',
        'building.room1': 'Room 1',
        'building.room2': 'Room 2',
        'building.room3': 'Room 3',
        'building.ambient': 'Outdoor',
        'common.unknown': 'Unknown'
      };
      return fallbacks[key] || key;
    };

    if (tagId.includes('TZ1')) return translate('building.thermalZone1');
    if (tagId.includes('R1')) return translate('building.room1');
    if (tagId.includes('R2')) return translate('building.room2');
    if (tagId.includes('R3')) return translate('building.room3');
    if (tagId.includes('Ambient')) return translate('building.ambient');
    return translate('common.unknown');
  }

  // Get comprehensive energy data
  getComprehensiveEnergyData(): ComprehensiveEnergyData {
    return {
      energy: this.getEnergySummary(),
      battery: this.getBatteryStatus(),
      evCharging: this.getEVChargingStatus(),
      heatPumps: this.getHeatPumpStatus(),
      building: this.getBuildingZones(),
      grid: this.getGridStatus(),
      environmental: this.getEnvironmentalData(),
      systemHealth: this.getSystemHealth()
    };
  }

  // Get battery status
  getBatteryStatus(): BatteryStatus {
    const bt1Soc = this.getLatestValue('BT1ChargeStatePercent');
    const bt1ChargePower = this.getLatestValue('BT1ChargeDCPower');
    const bt1MaxChargeRate = this.getLatestValue('BT1MaxChargeRate');
    const bt1MaxDischargeRate = this.getLatestValue('BT1MaxDischargeRate');
    const bt1OperatingStatus = this.getLatestValue('BT1OperatingStatus');
    const bt1ControlMode = this.getLatestValue('BT1StorageControlMode');

    // Calculate discharge power from energy (if we have historical data)
    let dischargePower = 0;
    if (bt1OperatingStatus === 2) { // Discharging status
      dischargePower = Math.abs(bt1ChargePower) || bt1MaxDischargeRate || 0;
    }

    return {
      soc: bt1Soc || 0,
      chargePower: bt1ChargePower || 0,
      dischargePower: dischargePower,
      storedEnergy: this.getLatestValue('BT1ChargeStateEnergy') || 0, // Use actual stored energy from JSON data
      maxChargeRate: bt1MaxChargeRate || 5,
      maxDischargeRate: bt1MaxDischargeRate || 5,
      operatingStatus: this.getStatusText('BT1OperatingStatus', bt1OperatingStatus) || 'Unknown',
      controlMode: this.getStatusText('BT1StorageControlMode', bt1ControlMode) || 'Unknown'
    };
  }

  // Get battery time series data for charts
  getBatteryTimeSeriesData(): { socData: number[], powerData: number[], timeLabels: string[] } {
    const socTag = this.data.find(d => d.tag.id === 'BT1ChargeStatePercent');
    const powerTag = this.data.find(d => d.tag.id === 'BT1ChargeDCPower');
    
    if (!socTag || !powerTag) {
      return { socData: [], powerData: [], timeLabels: [] };
    }

    // Get the latest samples (last 24 hours if available)
    const maxSamples = Math.min(24, socTag.samples.length);
    const recentSamples = socTag.samples.slice(-maxSamples);
    const recentPowerSamples = powerTag.samples.slice(-maxSamples);

    const socData = recentSamples.map(sample => sample.value);
    const powerData = recentPowerSamples.map(sample => sample.value);
    const timeLabels = recentSamples.map(sample => {
      const date = new Date(sample.utc);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    });

    return { socData, powerData, timeLabels };
  }

  // Get PV time series data for charts
  getPVTimeSeriesData(): { pv1Data: number[], pv2Data: number[], timeLabels: string[] } {
    const pv1Tag = this.data.find(d => d.tag.id === 'PV1Power');
    const pv2Tag = this.data.find(d => d.tag.id === 'PV2Power');
    
    if (!pv1Tag || !pv2Tag) {
      return { pv1Data: [], pv2Data: [], timeLabels: [] };
    }

    const maxSamples = Math.min(24, pv1Tag.samples.length);
    const recentSamples = pv1Tag.samples.slice(-maxSamples);
    const recentPv2Samples = pv2Tag.samples.slice(-maxSamples);

    const pv1Data = recentSamples.map(sample => sample.value);
    const pv2Data = recentPv2Samples.map(sample => sample.value);
    const timeLabels = recentSamples.map(sample => {
      const date = new Date(sample.utc);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    });

    return { pv1Data, pv2Data, timeLabels };
  }

  // Get consumer time series data for charts
  getConsumerTimeSeriesData(): { consumptionData: number[], timeLabels: string[] } {
    const consumerTag = this.data.find(d => d.tag.id === 'ConsumerPower');
    
    if (!consumerTag) {
      return { consumptionData: [], timeLabels: [] };
    }

    const maxSamples = Math.min(24, consumerTag.samples.length);
    const recentSamples = consumerTag.samples.slice(-maxSamples);

    const consumptionData = recentSamples.map(sample => sample.value);
    const timeLabels = recentSamples.map(sample => {
      const date = new Date(sample.utc);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    });

    return { consumptionData, timeLabels };
  }

  // Get grid time series data for charts
  getGridTimeSeriesData(): { importData: number[], exportData: number[], timeLabels: string[] } {
    const gridTag = this.data.find(d => d.tag.id === 'EM1Power');
    
    if (!gridTag) {
      return { importData: [], exportData: [], timeLabels: [] };
    }

    const maxSamples = Math.min(24, gridTag.samples.length);
    const recentSamples = gridTag.samples.slice(-maxSamples);

    const importData = recentSamples.map(sample => Math.max(0, sample.value));
    const exportData = recentSamples.map(sample => Math.max(0, -sample.value));
    const timeLabels = recentSamples.map(sample => {
      const date = new Date(sample.utc);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    });

    return { importData, exportData, timeLabels };
  }

  // Get heat pump time series data for charts
  getHeatPumpTimeSeriesData(): { hp1Data: number[], hp2Data: number[], timeLabels: string[] } {
    const hp1Tag = this.data.find(d => d.tag.id === 'HP1Power');
    const hp2Tag = this.data.find(d => d.tag.id === 'HP2Power');
    
    if (!hp1Tag || !hp2Tag) {
      return { hp1Data: [], hp2Data: [], timeLabels: [] };
    }

    const maxSamples = Math.min(24, hp1Tag.samples.length);
    const recentSamples = hp1Tag.samples.slice(-maxSamples);
    const recentHp2Samples = hp2Tag.samples.slice(-maxSamples);

    const hp1Data = recentSamples.map(sample => sample.value);
    const hp2Data = recentHp2Samples.map(sample => sample.value);
    const timeLabels = recentSamples.map(sample => {
      const date = new Date(sample.utc);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    });

    return { hp1Data, hp2Data, timeLabels };
  }

  // Get EV charging time series data for charts
  getEVChargingTimeSeriesData(): { ec1Data: number[], ec2Data: number[], timeLabels: string[] } {
    const ec1Tag = this.data.find(d => d.tag.id === 'EC1Power');
    const ec2Tag = this.data.find(d => d.tag.id === 'EC2Power');
    
    if (!ec1Tag || !ec2Tag) {
      return { ec1Data: [], ec2Data: [], timeLabels: [] };
    }

    const maxSamples = Math.min(24, ec1Tag.samples.length);
    const recentSamples = ec1Tag.samples.slice(-maxSamples);
    const recentEc2Samples = ec2Tag.samples.slice(-maxSamples);

    const ec1Data = recentSamples.map(sample => sample.value);
    const ec2Data = recentEc2Samples.map(sample => sample.value);
    const timeLabels = recentSamples.map(sample => {
      const date = new Date(sample.utc);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    });

    return { ec1Data, ec2Data, timeLabels };
  }

  // Get building temperature time series data for charts
  getBuildingTimeSeriesData(): { tz1Data: number[], r1Data: number[], r2Data: number[], r3Data: number[], ambientData: number[], timeLabels: string[] } {
    const tz1Tag = this.data.find(d => d.tag.id === 'TZ1Temp');
    const r1Tag = this.data.find(d => d.tag.id === 'R1Temp');
    const r2Tag = this.data.find(d => d.tag.id === 'R2Temp');
    const r3Tag = this.data.find(d => d.tag.id === 'R3Temp');
    const ambientTag = this.data.find(d => d.tag.id === 'AmbientTemp');
    
    if (!tz1Tag || !r1Tag || !r2Tag || !r3Tag) {
      return { tz1Data: [], r1Data: [], r2Data: [], r3Data: [], ambientData: [], timeLabels: [] };
    }

    const maxSamples = Math.min(24, tz1Tag.samples.length);
    const recentTz1Samples = tz1Tag.samples.slice(-maxSamples);
    const recentR1Samples = r1Tag.samples.slice(-maxSamples);
    const recentR2Samples = r2Tag.samples.slice(-maxSamples);
    const recentR3Samples = r3Tag.samples.slice(-maxSamples);
    const recentAmbientSamples = ambientTag ? ambientTag.samples.slice(-maxSamples) : [];

    const tz1Data = recentTz1Samples.map(sample => sample.value);
    const r1Data = recentR1Samples.map(sample => sample.value);
    const r2Data = recentR2Samples.map(sample => sample.value);
    const r3Data = recentR3Samples.map(sample => sample.value);
    const ambientData = recentAmbientSamples.length > 0 ? recentAmbientSamples.map(sample => sample.value) : [];
    const timeLabels = recentTz1Samples.map(sample => {
      const date = new Date(sample.utc);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    });

    return { tz1Data, r1Data, r2Data, r3Data, ambientData, timeLabels };
  }

  // Get EV charging status
  getEVChargingStatus(): EVChargingStatus {
    return {
      ec1: {
        state: this.getStatusText('EC1ChargerState', this.getLatestValue('EC1ChargerState')) || 'Unknown',
        power: this.getLatestValue('EC1Power') || 0,
        energy: this.getLatestValue('EC1Energy') || 0,
        vehicleState: this.getStatusText('EC1VehicleState', this.getLatestValue('EC1VehicleState')) || 'Unknown',
        current: {
          p1: this.getLatestValue('EC1CurrentP1') || 0,
          p2: this.getLatestValue('EC1CurrentP2') || 0,
          p3: this.getLatestValue('EC1CurrentP3') || 0
        },
        voltage: {
          p1: this.getLatestValue('EC1VoltageP1') || 0,
          p2: this.getLatestValue('EC1VoltageP2') || 0,
          p3: this.getLatestValue('EC1VoltageP3') || 0
        }
      },
      ec2: {
        state: this.getStatusText('EC2ChargerState', this.getLatestValue('EC2ChargerState')) || 'Unknown',
        power: this.getLatestValue('EC2Power') || 0,
        energy: this.getLatestValue('EC2Energy') || 0,
        vehicleState: this.getStatusText('EC2VehicleState', this.getLatestValue('EC2VehicleState')) || 'Unknown',
        current: {
          p1: this.getLatestValue('EC2CurrentP1') || 0,
          p2: this.getLatestValue('EC2CurrentP2') || 0,
          p3: this.getLatestValue('EC2CurrentP3') || 0
        },
        voltage: {
          p1: this.getLatestValue('EC2VoltageP1') || 0,
          p2: this.getLatestValue('EC2VoltageP2') || 0,
          p3: this.getLatestValue('EC2VoltageP3') || 0
        }
      }
    };
  }

  // Get heat pump status
  getHeatPumpStatus(): HeatPumpStatus {
    return {
      hp1: {
        power: this.getLatestValue('HP1Power') || 0,
        status: this.getStatusText('HP1Status', this.getLatestValue('HP1Status')) || 'Unknown',
        operatingMode: this.getStatusText('HP1OperatingModeReading', this.getLatestValue('HP1OperatingModeReading')) || 'Unknown',
        bt2: this.getLatestValue('HP1BT2') || 0,
        bt3: this.getLatestValue('HP1BT3') || 0,
        highPressure: this.getLatestValue('HP1HighPressure') || 0,
        lowPressure: this.getLatestValue('HP1LowPressure') || 0
      },
      hp2: {
        power: this.getLatestValue('HP2Power') || 0,
        status: this.getStatusText('HP2Status', this.getLatestValue('HP2Status')) || 'Unknown',
        operatingMode: this.getStatusText('HP2OperatingModeReading', this.getLatestValue('HP2OperatingModeReading')) || 'Unknown',
        bt2: this.getLatestValue('HP2BT2') || 0,
        bt3: this.getLatestValue('HP2BT3') || 0,
        highPressure: this.getLatestValue('HP2HighPressure') || 0,
        lowPressure: this.getLatestValue('HP2LowPressure') || 0
      }
    };
  }

  // Get building zones
  getBuildingZones(): BuildingZones {
    return {
      tz1: {
        power: this.getLatestValue('TZ1Power') || 0,
        temperature: this.getLatestValue('TZ1Temp') || 0,
        seasonMode: this.getStatusText('TZ1SeasonMode', this.getLatestValue('TZ1SeasonMode')) || 'Unknown',
        supplyTempExpected: this.getLatestValue('TZ1SupplyTempExpected') || 0
      },
      rooms: this.getRoomTemperatures()
    };
  }

  // Get grid status
  getGridStatus(): GridStatus {
    const importPower = this.getLatestValue('EM1Power') || 0;
    const exportPower = Math.abs(Math.min(0, importPower));
    const actualImport = Math.max(0, importPower);
    const importTariff = this.getLatestValue('EM1ImportTariff') || 0;
    const exportTariff = this.getLatestValue('EM1ExportTariff') || 0;

    return {
      importPower: actualImport,
      exportPower: exportPower,
      netPower: importPower,
      importTariff: importTariff,
      exportTariff: exportTariff,
      importEnergy: this.getLatestValue('EM1EnergyImport') || 0,
      exportEnergy: this.getLatestValue('EM1EnergyExport') || 0
    };
  }

  // Get environmental data
  getEnvironmentalData(): EnvironmentalData {
    return {
      ambientTemp: this.getLatestValue('AmbientTemp') || 0,
      solarRadiation: this.getLatestValue('AmbientRadiationExpected') || 0,
      radiationExpected: this.getLatestValue('AmbientRadiationExpected') || 0
    };
  }

  // Get system health
  getSystemHealth(): SystemHealth {
    return {
      cs1: {
        cpuTemperature: this.getLatestValue('CS1SystemCpuTemperature') || 0,
        memoryAvailable: this.getLatestValue('CS1SystemMemoryAvailable') || 0,
        memoryTotal: this.getLatestValue('CS1SystemMemoryTotal') || 0,
        cpuUsage: this.getLatestValue('CS1SystemCpuUserTime') || 0
      }
    };
  }

  // Helper method to get latest value for a tag
  private getLatestValue(tagId: string): number {
    const data = this.data.find(d => d.tag.id === tagId);
    if (data && data.samples.length > 0) {
      return data.samples[data.samples.length - 1].value;
    }
    return 0;
  }

  // Helper method to get status text
  private getStatusText(tagId: string, value: number): string {
    const data = this.data.find(d => d.tag.id === tagId);
    if (data && data.tag.status) {
      return data.tag.status[value.toString()] || 'Unknown';
    }
    return 'Unknown';
  }

  // Process natural language query
  // Note: This method should be replaced with API calls to the backend in production
  // For now, it uses i18n for translations but requires i18n to be initialized
  processQuery(query: string, t?: any): { type: string; data: any; response: string } {
    const lowerQuery = query.toLowerCase();
    
    // Use translation function if provided, otherwise fallback to English
    // Wrap to ensure we always get a string
    const translate = (key: string, options?: any): string => {
      if (t) {
        const result = t(key, options);
        // Ensure we return a string (i18next can return objects)
        return typeof result === 'string' ? result : String(result);
      }
      // Fallback translations in English
      const fallbacks: { [key: string]: string } = {
        'chat.responses.pvProduction': `Your PV system produced ${options?.value || 0} kWh today.`,
        'chat.responses.consumptionToday': `Your power consumption today is ${options?.value || 0} kWh.`,
        'chat.responses.consumption7Days': `Your power consumption in the last 7 days is ${options?.value || 0} kWh.`,
        'chat.responses.roomTemperatures': `Current room temperatures: ${options?.temperatures || ''}`,
        'chat.responses.unknown': 'Sorry, I don\'t understand your question. Try asking about PV production, power consumption, or battery information.',
        'building.thermalZone1': 'Living Room',
        'building.room1': 'Room 1',
        'building.room2': 'Room 2',
        'building.room3': 'Room 3',
        'building.ambient': 'Outdoor',
        'common.unknown': 'Unknown'
      };
      return fallbacks[key] || key;
    };

    // Check for PV production (English and German keywords)
    if (lowerQuery.includes('pv') || lowerQuery.includes('solar') || 
        lowerQuery.includes('produktion') || lowerQuery.includes('production')) {
      const production = this.getPVProductionToday();
      return {
        type: 'pv_production',
        data: { value: production, unit: 'kWh' },
        response: translate('chat.responses.pvProduction', { value: production })
      };
    }

    // Check for consumption today (English and German keywords)
    if ((lowerQuery.includes('consumption') || lowerQuery.includes('verbrauch')) && 
        (lowerQuery.includes('today') || lowerQuery.includes('heute'))) {
      const consumption = this.getConsumptionToday();
      return {
        type: 'consumption',
        data: { value: consumption, unit: 'kWh' },
        response: translate('chat.responses.consumptionToday', { value: consumption })
      };
    }

    // Check for consumption last 7 days (English and German keywords)
    if ((lowerQuery.includes('consumption') || lowerQuery.includes('verbrauch')) && 
        (lowerQuery.includes('7') || lowerQuery.includes('woche') || lowerQuery.includes('week') || lowerQuery.includes('days'))) {
      const consumption = this.getConsumption7Days();
      return {
        type: 'consumption_7days',
        data: { value: consumption, unit: 'kWh' },
        response: translate('chat.responses.consumption7Days', { value: consumption })
      };
    }

    // Check for room temperatures (English and German keywords)
    if (lowerQuery.includes('temperatur') || lowerQuery.includes('temperature') || 
        lowerQuery.includes('zimmer') || lowerQuery.includes('room')) {
      // Pass translation function to getRoomTemperatures for localized room names
      const temperatures = this.getRoomTemperatures(translate);
      const tempList = temperatures.map(temp => `${temp.room}: ${temp.temperature}°C`).join(', ');
      return {
        type: 'room_temperature',
        data: { temperatures },
        response: translate('chat.responses.roomTemperatures', { temperatures: tempList })
      };
    }

    return {
      type: 'unknown',
      data: null,
      response: translate('chat.responses.unknown')
    };
  }
}
